import { useState, useMemo, useRef } from 'react';

/**
 * A simplified hook that manages pagination state
 * of the product table display
 */
const usePagination = ({
  itemsPerPage = 100, // default
  totalItems = 0,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPageValue, setItemsPerPageValue] = useState(itemsPerPage);
  const prevTotalItemsRef = useRef(totalItems);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalItems / itemsPerPageValue)),
    [totalItems, itemsPerPageValue],
  );

  // Synchronous reset — no extra render, no flash of empty table
  if (totalItems !== prevTotalItemsRef.current) {
    prevTotalItemsRef.current = totalItems;
    setCurrentPage(1);
  }

  // Clamp in case currentPage exceeds totalPages (e.g. items removed from current page)
  const safePage = Math.min(currentPage, totalPages) || 1;

  const visibleIndices = useMemo(() => {
    const startIndex = (safePage - 1) * itemsPerPageValue;
    const endIndex = Math.min(startIndex + itemsPerPageValue, totalItems);
    return [startIndex, endIndex];
  }, [safePage, itemsPerPageValue, totalItems]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPageValue(newItemsPerPage);
    resetToFirstPage();
  };

  return {
    currentPage: safePage,
    totalPages,
    itemsPerPage: itemsPerPageValue,
    visibleIndices,
    handlePageChange,
    handleItemsPerPageChange,
    resetToFirstPage,
  };
};

export default usePagination;
